import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * @typedef {import('@shopify/events').CartAjaxLine & {
 *   properties?: Record<string, string>
 * }} CartLine
 */

/**
 * @typedef {Object} CartCharityRefs
 * @property {HTMLElement} charityError
 *
 * @extends {Component<CartCharityRefs>}
 */
class CartCharity extends Component {
  requiredRefs = ['charityError'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Promise<boolean> | null} */
  #pendingWrite = null;

  /** @type {boolean} */
  #ready = false;

 connectedCallback() {
  super.connectedCallback();

  const select = this.querySelector('select');

  if (select instanceof HTMLSelectElement) {
    select.addEventListener('change', this.updateCharity);
  }

  document.addEventListener(
    StandardEvents.cartLinesUpdate,
    this.#onCartLinesUpdate
  );

  document.addEventListener(
    'submit',
    this.#onCartFormSubmit,
    { capture: true }
  );

  this.#setCheckoutReady(false);

  queueMicrotask(() => {
    this.#syncInitialState();
  });
}

disconnectedCallback() {
  const select = this.querySelector('select');

  if (select instanceof HTMLSelectElement) {
    select.removeEventListener('change', this.updateCharity);
  }

  document.removeEventListener(
    StandardEvents.cartLinesUpdate,
    this.#onCartLinesUpdate
  );

  document.removeEventListener(
    'submit',
    this.#onCartFormSubmit,
    { capture: true }
  );

  super.disconnectedCallback();
}

  /**
 * Handles a charity selection change.
 *
 * @param {Event} event
 */
  updateCharity = (event) => {
    const select = event.target;

    if (!(select instanceof HTMLSelectElement)) return;

    if (!select.value) {
      this.#setCheckoutReady(false);
      this.#showRequiredError(select);
      return;
    }

    /*
     * Disable checkout synchronously.
     *
     * This happens BEFORE the first await/fetch so there is no
     * opportunity to select a charity and immediately checkout.
     */
    this.#setCheckoutReady(false);

    const write = this.#applyCharitySelection(event, select);

    this.#trackWrite(write);
  };

  /**
   * Handles the case where:
   *
   * 1. charity was already selected previously, or
   * 2. the user changed the select before this component upgraded.
   */
  #syncInitialState() {
  const select = this.querySelector('select');

  if (!(select instanceof HTMLSelectElement)) return;

  const savedCharity = this.dataset.currentCharity ?? '';

  // No charity has actually been saved to the Shopify cart.
  if (!savedCharity) {
    // Reset any stale/browser-preserved selection.
    select.value = '';

    this.#setCheckoutReady(false);
    return;
  }

  // Make sure the dropdown reflects the actual Shopify cart attribute.
  select.value = savedCharity;

  // Verify all current lines contain the charity property
  // before checkout can be enabled.
  const write = this.#verifyCurrentLines(select);

  this.#trackWrite(write);
}

   

  /**
   * Track a write without allowing an older/aborted promise
   * to clear a newer one.
   *
   * @param {Promise<boolean>} write
   */
  #trackWrite(write) {
    this.#pendingWrite = write;

    write.then(
      (success) => {
        if (this.#pendingWrite !== write) return;

        this.#pendingWrite = null;

        /*
         * morphSection/renderSection may have replaced this component.
         * Don't let an obsolete instance enable the new checkout UI.
         */
        if (this.isConnected) {
          this.#setCheckoutReady(success);
        }
      },
      () => {
        if (this.#pendingWrite !== write) return;

        this.#pendingWrite = null;

        if (this.isConnected) {
          this.#setCheckoutReady(false);
        }
      }
    );
  }

  /**
   * Intercept the normal cart checkout form.
   *
   * Checkout only proceeds once Shopify has actually accepted
   * both the line-item property and cart attribute changes.
   *
   * @param {Event} event
   */
  #onCartFormSubmit = (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;
    if (form.id !== 'cart-form') return;

    const select = this.querySelector('select');

    if (!(select instanceof HTMLSelectElement)) return;

    if (!select.value) {
      event.preventDefault();
      this.#showRequiredError(select);
      return;
    }

    /*
     * Selected does NOT necessarily mean saved.
     */
    if (!this.#ready || this.#pendingWrite) {
      event.preventDefault();

      const pendingWrite = this.#pendingWrite;

      if (!pendingWrite) {
        this.#showSaveError(select);
        return;
      }

      /*
       * Wait for Shopify to accept the write.
       *
       * Only continue if it succeeded.
       */
      pendingWrite.then((success) => {
        if (!success) {
          this.#showSaveError(select);
          return;
        }

        const checkoutButton = document.querySelector(
          '[data-charity-checkout]'
        );

        /*
         * Use the CURRENT button because morphSection may have replaced
         * the button that originally triggered the submit.
         */
        if (
          checkoutButton instanceof HTMLButtonElement &&
          !checkoutButton.disabled
        ) {
          checkoutButton.click();
        }
      });
    }
  };

  /**
   * Apply a newly-selected charity.
   *
   * @param {Event | null} event
   * @param {HTMLSelectElement} select
   * @returns {Promise<boolean>}
   */
  async #applyCharitySelection(event, select) {
    const { charityError } = this.refs;

    charityError.hidden = true;

    const charityName = select.value;
    const charityTag =
      select.selectedOptions[0]?.dataset.tag ?? '';

    if (!charityName || !charityTag) {
      charityError.hidden = false;
      return false;
    }

    /*
     * Cancel an older selection write if the user changes
     * charities quickly.
     */
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      /*
       * IMPORTANT:
       * Stamp lines FIRST.
       *
       * The cart attribute doesn't become "ready" until every existing
       * line has the corresponding Charity property.
       */
      await this.#stampAllLines(
        charityTag,
        abortController.signal
      );

      const response = await fetch(
        Theme.routes.cart_update_url,
        {
          ...fetchConfig('json', {
            body: JSON.stringify({
              attributes: {
                Charity: charityName,
                'Charity Tag': charityTag,
              },
              sections: this.dataset.sectionId
                ? [this.dataset.sectionId]
                : [],
            }),
          }),
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to update cart attributes: ${response.status}`
        );
      }

      const data = await response.json();

      const sectionId = this.dataset.sectionId;

      if (sectionId) {
        const sectionHtml = data.sections?.[sectionId];

        if (sectionHtml) {
          morphSection(
            sectionId,
            sectionHtml,
            { mode: 'full' }
          );
        }
      }

      return true;
    } catch (error) {
      if (
        error instanceof Error &&
        error.name === 'AbortError'
      ) {
        return false;
      }

      this.#handleError(error);

      return false;
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }

      if (event) {
        cartPerformance.measureFromEvent(
          'charity-update:user-action',
          event
        );
      }
    }
  }

  /**
   * Existing cart attribute is valid, but ensure every current
   * cart line also has the property.
   *
   * @param {HTMLSelectElement} select
   * @returns {Promise<boolean>}
   */
  async #verifyCurrentLines(select) {
    const charityTag =
      select.selectedOptions[0]?.dataset.tag ?? '';

    if (!charityTag) return false;

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const changed = await this.#stampAllLines(
        charityTag,
        abortController.signal
      );

      const sectionId = this.dataset.sectionId;

      /*
       * Refresh the cart UI if we repaired a missing line property.
       */
      if (changed && sectionId) {
        await sectionRenderer.renderSection(
          sectionId,
          {
            cache: false,
            mode: 'full',
          }
        );
      }

      return true;
    } catch (error) {
      if (
        error instanceof Error &&
        error.name === 'AbortError'
      ) {
        return false;
      }

      this.#handleError(error);

      return false;
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
    }
  }

  /**
   * A cart line changed/was added.
   *
   * Mark checkout unsafe IMMEDIATELY, rather than waiting for
   * event.promise first.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    const select = this.querySelector('select');

    if (!(select instanceof HTMLSelectElement)) return;

    if (!select.value) {
      this.#setCheckoutReady(false);
      return;
    }

    const charityTag =
      select.selectedOptions[0]?.dataset.tag ?? '';

    if (!charityTag) {
      this.#setCheckoutReady(false);
      return;
    }

    /*
     * This must happen synchronously.
     */
    this.#setCheckoutReady(false);

    const write = (async () => {
      try {
        await event.promise;

        const stamped =
          await this.#stampAllLines(charityTag);

        const sectionId = this.dataset.sectionId;

        if (stamped && sectionId) {
          await sectionRenderer.renderSection(
            sectionId,
            {
              cache: false,
              mode: 'full',
            }
          );
        }

        return true;
      } catch (error) {
        this.#handleError(error);
        return false;
      }
    })();

    this.#trackWrite(write);
  };

  /**
   * Set the Charity line-item property on every cart line.
   *
   * Shopify change.js only updates one cart line at a time.
   *
   * @param {string} charityTag
   * @param {AbortSignal} [signal]
   * @returns {Promise<boolean>}
   */
  async #stampAllLines(charityTag, signal) {
    const cartResponse = await fetch(
      `${Theme.routes.cart_url}.json`,
      {
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        signal,
      }
    );

    if (!cartResponse.ok) {
      throw new Error(
        `Failed to fetch cart: ${cartResponse.status}`
      );
    }

    /** @type {{ items: CartLine[] }} */
    const cart = await cartResponse.json();

    const linesToUpdate = cart.items.filter(
      (item) =>
        item.properties?.Charity !== charityTag
    );

    for (const item of linesToUpdate) {
      const response = await fetch(
        Theme.routes.cart_change_url,
        {
          ...fetchConfig('json', {
            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity,

              /*
               * change.js replaces the entire properties object,
               * so retain the existing properties.
               */
              properties: {
                ...item.properties,
                Charity: charityTag,
              },
            }),
          }),
          signal,
        }
      );

      if (!response.ok) {
        const body = await response.text();

        throw new Error(
          `Failed to set Charity property on line ${item.key}: ` +
          `${response.status} ${body}`
        );
      }
    }

    return linesToUpdate.length > 0;
  }

  /**
   * Enable/disable every route to checkout controlled by the cart UI.
   *
   * @param {boolean} ready
   */
  #setCheckoutReady(ready) {
    this.#ready = ready;

    const checkoutButton = document.querySelector(
      '[data-charity-checkout]'
    );

    if (checkoutButton instanceof HTMLButtonElement) {
      const cartEmpty =
        checkoutButton.dataset.cartEmpty === 'true';

      checkoutButton.disabled =
        cartEmpty || !ready;

      checkoutButton.setAttribute(
        'aria-disabled',
        String(cartEmpty || !ready)
      );
    }

    document
      .querySelectorAll('[data-charity-accelerated]')
      .forEach((element) => {
        if (!(element instanceof HTMLElement)) return;

        element.hidden = !ready;
        element.toggleAttribute('inert', !ready);

        if (ready) {
          element.removeAttribute('aria-disabled');
        } else {
          element.setAttribute(
            'aria-disabled',
            'true'
          );
        }
      });
  }

  /**
   * @param {HTMLSelectElement} select
   */
  #showRequiredError(select) {
    this.refs.charityError.hidden = false;

    select.focus();
    select.reportValidity();
  }

  /**
   * @param {HTMLSelectElement} select
   */
  #showSaveError(select) {
    this.refs.charityError.hidden = false;
    select.focus();
  }

  /**
   * @param {unknown} error
   */
  #handleError(error) {
    console.error(
      '[cart-charity] Charity update failed:',
      error
    );

    this.refs.charityError.hidden = false;

    const message =
      error instanceof Error
        ? error.message
        : 'Failed to update charity selection';

    this.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  }
}

if (
  !customElements.get(
    'cart-charity-component'
  )
) {
  customElements.define(
    'cart-charity-component',
    CartCharity
  );
}